import { useEffect, useRef, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { handleUrlChange, setPageType, setDataLoading } from '../redux/slices/filterSlice';
import { debounce } from 'lodash';

export const useUrlChangeHandler = () => {
  const dispatch = useDispatch();
  const { dataLoading } = useSelector(state => state.filter);
  const originalPushState = useRef(null);
  const isInitialMount = useRef(true);
  const hasInitialized = useRef(false);

  // Memoize getPageType to ensure consistent reference
  const getPageType = useMemo(() => () => {
    const path = window.location.pathname;
    if (path === '/browse') return 'browse';
    if (path === '/dashboard/listings') return 'listings';
    if (path === '/favorites') return 'favorites';
    if (path.includes('/listing/')) return 'listingForm';
    if (path.includes('/merchant/')) {
      console.log('useUrlChangeHandler detected merchant page:', path);
      return 'merchant';
    }
    return null;
  }, []);

  // Memoize the current page type
  const currentPageType = useMemo(() => getPageType(), [getPageType]);

  // Memoize dispatch handlers to prevent recreation
  const handlePageTypeChange = useCallback((pageType) => {
    if (pageType) {
      console.log('Setting page type to:', pageType);
      dispatch(setPageType(pageType));
      if (dataLoading) {
        dispatch(setDataLoading(false));
      }
    }
  }, [dispatch, dataLoading]);

  const handleSearch = useCallback((pageType) => {
    if (pageType) {
      console.log('Handling URL change for page type:', pageType);
      dispatch(handleUrlChange());
    }
  }, [dispatch]);

  // Memoize debounced search to maintain stable reference
  const debouncedSearch = useMemo(
    () => debounce(handleSearch, 300),
    [handleSearch]
  );

  // Initial setup effect - runs once
  useEffect(() => {
    // Handle initial mount
    if (isInitialMount.current) {
      handlePageTypeChange(currentPageType);
      if (!hasInitialized.current) {
        debouncedSearch(currentPageType);
        hasInitialized.current = true;
      }
      isInitialMount.current = false;
    }

    // Cleanup
    return () => {
      handlePageTypeChange(null);
      isInitialMount.current = true;
      hasInitialized.current = false;
      debouncedSearch.cancel();
    };
  }, [currentPageType, handlePageTypeChange, debouncedSearch]);

  // URL change handling effect - runs once
  useEffect(() => {
    if (!originalPushState.current) {
      originalPushState.current = window.history.pushState;
      window.history.pushState = function() {
        originalPushState.current.apply(this, arguments);
        const newPageType = getPageType();
        if (newPageType !== currentPageType) {
          // Reset everything when page type changes
          handlePageTypeChange(newPageType);
          
          // When changing page types, we need to force a clean slate
          if (
            (currentPageType === 'browse' && newPageType === 'favorites') ||
            (currentPageType === 'favorites' && newPageType === 'browse') ||
            (currentPageType === 'listingForm' && newPageType === 'listings') ||
            (currentPageType === 'browse' && newPageType === 'merchant') ||
            (currentPageType === 'merchant' && newPageType === 'browse')
          ) {
            dispatch(setDataLoading(true)); // Set loading state
            // Small delay to ensure loading state is visible
            setTimeout(() => {
              debouncedSearch(newPageType);
            }, 50);
          } else {
            debouncedSearch(newPageType);
          }
        }
      };

      const handlePopState = () => {
        const newPageType = getPageType();
        if (newPageType !== currentPageType) {
          // Reset everything when page type changes
          handlePageTypeChange(newPageType);
          
          // When changing page types, we need to force a clean slate
          if (
            (currentPageType === 'browse' && newPageType === 'favorites') ||
            (currentPageType === 'favorites' && newPageType === 'browse') ||
            (currentPageType === 'listingForm' && newPageType === 'listings') ||
            (currentPageType === 'browse' && newPageType === 'merchant') ||
            (currentPageType === 'merchant' && newPageType === 'browse')
          ) {
            dispatch(setDataLoading(true)); // Set loading state
            // Small delay to ensure loading state is visible
            setTimeout(() => {
              debouncedSearch(newPageType);
            }, 50);
          } else {
            debouncedSearch(newPageType);
          }
        }
      };

      window.addEventListener('popstate', handlePopState);

      return () => {
        window.removeEventListener('popstate', handlePopState);
        if (originalPushState.current && window.history.pushState !== originalPushState.current) {
          window.history.pushState = originalPushState.current;
        }
      };
    }
  }, [getPageType, currentPageType, handlePageTypeChange, debouncedSearch]);
};

// Support both named and default exports for backward compatibility
export default useUrlChangeHandler; 